import { type CSSProperties, useState } from 'react';

import styles from './CustomDash.module.css';
import { CustomDashPageSpecificWrappers } from './CustomDashPageSpecificWrappers';
import { DataResolver } from './components/DataResolver/DataResolver';
import { EditableGrid } from './components/EditableGrid/EditableGrid';
import { useEditMode } from './components/EditModeProvider/EditModeProvider';
import { KeepAliveTabGrids } from './components/KeepAliveTabGrids/KeepAliveTabGrids';
import { useTabContentConfig } from './components/PageConfigProvider/PageConfigProvider';
import { ReportFlowEditor } from './components/ReportFlow/ReportFlowEditor';
import { SharedReportDataProvider } from './components/SharedReport/SharedReportDataContext';
import { TableSelectionProvider } from './components/TableSelectionProvider/TableSelectionProvider';
import { TextChartContentProvider } from './components/TextChartContentProvider/TextChartContentProvider';
import { TextChartOrchestrator } from './components/TextChartContentProvider/TextChartOrchestrator';
import { SIDE_PANEL_WIDTH } from './constants/panelWidths';
import { usePostingsVolumeChartToggle } from './hooks/usePostingsVolumeChartToggle';
import { useRemovedSavedViewFilter } from './hooks/useRemovedSavedViewFilter';
import { useSentimentItemsOverride } from './hooks/useSentimentItemsOverride';
import { useSyncActiveFilterGroup } from './hooks/useSyncActiveFilterGroup';
import { useSyncPrimaryEntities } from './hooks/useSyncPrimaryEntities';
import { useOptionalReportDocument } from './report-document/report-document-context';

interface CustomDashProps {
  tabContentId: string;
}

export const CustomDash = ({ tabContentId }: CustomDashProps) => {
  const { editMode } = useEditMode();
  const { items } = useTabContentConfig();
  const isFlowReport = useOptionalReportDocument() !== null;

  const [isBuilderOpen, setIsBuilderOpen] = useState(false);

  useSentimentItemsOverride();
  usePostingsVolumeChartToggle();
  useSyncActiveFilterGroup();
  useRemovedSavedViewFilter();
  useSyncPrimaryEntities();

  const handleBuilderOpenChange = (isOpen: boolean) => {
    setIsBuilderOpen(isOpen);
  };

  const gridContent = (() => {
    if (isFlowReport) {
      return (
        <ReportFlowEditor
          readOnly={!editMode}
          onBuilderOpenChange={handleBuilderOpenChange}
        />
      );
    }
    if (editMode) {
      return (
        <DataResolver key={tabContentId} items={items}>
          <EditableGrid onBuilderOpenChange={handleBuilderOpenChange} />
        </DataResolver>
      );
    }
    return <KeepAliveTabGrids />;
  })();

  return (
    <SharedReportDataProvider>
      {/* Not keyed by tab: the provider must persist across tab switches so the
          view-mode keep-alive grids below it stay mounted. */}
      <TableSelectionProvider>
        <TextChartContentProvider>
          <div
            className={styles.dashboard}
            style={
              {
                '--custom-dash-width': isBuilderOpen
                  ? `calc(100% - ${SIDE_PANEL_WIDTH})`
                  : '100%',
              } as CSSProperties
            }
          >
            <CustomDashPageSpecificWrappers>
              <TextChartOrchestrator />
              {gridContent}
            </CustomDashPageSpecificWrappers>
          </div>
        </TextChartContentProvider>
      </TableSelectionProvider>
    </SharedReportDataProvider>
  );
};
